/**
 * useOutboxReplay - Replays pending list membership intents from the outbox
 *
 * Triggers replay:
 * - On app boot after hydration
 * - On reconnect (online event)
 * - On window focus
 * - Periodically every 5s while there are pending intents
 *
 * Clears outbox on:
 * - Logout (userId becomes null)
 * - User switch (userId changes)
 *
 * Uses last-intent-wins semantics and idempotent server operations.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  getListMembershipIntentsToReplay,
  getFollowIntentsToReplay,
  getReorderIntentsToReplay,
  removeIntent,
  updateIntent,
  calculateRetryDelay,
  getOutboxCount,
  clearOutbox,
  getFollowIntentKeyForTarget,
  getReorderIntentKeyForLookup,
  type ListMembershipIntent,
  type UserFollowIntent,
  type ListReorderMoveIntent,
} from '../lib/outbox';
import { apiFetch, ApiError } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { WatchlistMeta } from './useListsQuery';

const REPLAY_INTERVAL_MS = 5000; // 5 seconds while pending
const INVALIDATION_DEBOUNCE_MS = 500;
const DEFAULT_LIST_SLUGS = ['watched', 'watchlist', 'favorites'] as const;
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export function useOutboxReplay() {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  const isReplayingRef = useRef(false);
  const replayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const invalidationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInvalidationsRef = useRef<Set<string>>(new Set());
  const prevUserIdRef = useRef<string | null>(null);

  /**
   * Stop all replay activity (intervals + timeouts)
   */
  const stopReplay = useCallback(() => {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
    if (invalidationTimeoutRef.current) {
      clearTimeout(invalidationTimeoutRef.current);
      invalidationTimeoutRef.current = null;
    }
    pendingInvalidationsRef.current.clear();
    isReplayingRef.current = false;
  }, []);

  /**
   * Resolve default list slug to actual UUID for invalidation.
   * Returns the slug if no UUID found (fallback, won't invalidate wrong data).
   */
  const resolveListIdForInvalidation = useCallback((intentListId: string, isDefaultList: boolean): string => {
    if (!isDefaultList || !DEFAULT_LIST_SLUGS.includes(intentListId as typeof DEFAULT_LIST_SLUGS[number])) {
      return intentListId; // Custom list - use UUID directly
    }

    // Default list - look up actual UUID from lists.mine cache
    if (!user?.id) return intentListId;

    const listsData = queryClient.getQueryData<WatchlistMeta[]>(queryKeys.lists.mine(user.id));
    if (!listsData) return intentListId;

    const defaultList = listsData.find((l) => l.slug === intentListId && l.is_default);
    return defaultList?.id || intentListId;
  }, [user?.id, queryClient]);

  /**
   * Debounced invalidation to prevent refetch storms (for list membership only)
   */
  const scheduleInvalidation = useCallback((intent: ListMembershipIntent) => {
    // Resolve to actual list UUID for default lists
    const actualListId = resolveListIdForInvalidation(intent.listId, intent.isDefaultList);
    pendingInvalidationsRef.current.add(actualListId);

    if (invalidationTimeoutRef.current) {
      clearTimeout(invalidationTimeoutRef.current);
    }

    invalidationTimeoutRef.current = setTimeout(() => {
      const listIds = Array.from(pendingInvalidationsRef.current);
      pendingInvalidationsRef.current.clear();

      // Invalidate lists.mine once (not per-list)
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.lists.mine(user.id) });
      }

      // Invalidate specific list items (using resolved UUIDs)
      for (const id of listIds) {
        queryClient.invalidateQueries({ queryKey: ['lists', id], exact: false });
      }
    }, INVALIDATION_DEBOUNCE_MS);
  }, [user?.id, queryClient, resolveListIdForInvalidation]);

  /**
   * Replay a single list membership intent
   */
  const replayListMembershipIntent = useCallback(async (
    intent: ListMembershipIntent,
    token: string
  ): Promise<boolean> => {
    const intentKey = `${intent.listId}:${intent.mediaType}:${intent.mediaId}`;

    try {
      if (intent.desiredInList) {
        // ADD intent
        if (intent.isDefaultList && intent.listSlug) {
          // Default list: POST /watchlists/quick-add
          await apiFetch<{ item: { id: string }; duplicate?: boolean }>(
            '/watchlists/quick-add',
            {
              method: 'POST',
              token,
              headers: {
                'Idempotency-Key': intent.idempotencyKey,
              },
              body: {
                slug: intent.listSlug,
                media_id: intent.mediaId,
                media_type: intent.mediaType,
                media_title: intent.mediaTitle,
                media_poster: intent.mediaPoster,
                media_year: intent.mediaYear,
              },
            }
          );
        } else {
          // Custom list: POST /watchlists/:listId/items
          await apiFetch<{ item: { id: string }; duplicate?: boolean }>(
            `/watchlists/${intent.listId}/items`,
            {
              method: 'POST',
              token,
              headers: {
                'Idempotency-Key': intent.idempotencyKey,
              },
              body: {
                media_id: intent.mediaId,
                media_type: intent.mediaType,
                media_title: intent.mediaTitle,
                media_poster: intent.mediaPoster,
                media_year: intent.mediaYear,
              },
            }
          );
        }
      } else {
        // REMOVE intent
        if (intent.isDefaultList && intent.listSlug) {
          // Default list: DELETE /watchlists/default/by-media (idempotent)
          await apiFetch<{ success: boolean; deleted_count: number }>(
            '/watchlists/default/by-media',
            {
              method: 'DELETE',
              token,
              headers: {
                'Idempotency-Key': intent.idempotencyKey,
              },
              body: {
                slug: intent.listSlug,
                media_id: intent.mediaId,
                media_type: intent.mediaType,
              },
            }
          );
        } else {
          // Custom list: DELETE /watchlists/:listId/items/by-media (idempotent)
          await apiFetch<{ success: boolean; deleted_count: number }>(
            `/watchlists/${intent.listId}/items/by-media`,
            {
              method: 'DELETE',
              token,
              headers: {
                'Idempotency-Key': intent.idempotencyKey,
              },
              body: {
                media_id: intent.mediaId,
                media_type: intent.mediaType,
              },
            }
          );
        }
      }

      // Success - remove from outbox
      await removeIntent(intent.userId, intentKey);
      scheduleInvalidation(intent);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Check if it's a client error (4xx) that shouldn't be retried
      const is4xx = err instanceof Error && 'status' in err &&
        typeof (err as Error & { status: number }).status === 'number' &&
        (err as Error & { status: number }).status >= 400 &&
        (err as Error & { status: number }).status < 500;

      if (is4xx) {
        // Client error - remove from outbox (won't succeed on retry)
        console.warn('[OUTBOX] Removing failed intent (4xx):', intentKey, errorMessage);
        await removeIntent(intent.userId, intentKey);
        return false;
      }

      // Server error or network error - update for retry
      const nextAttempts = intent.attempts + 1;
      const nextRetryDelay = calculateRetryDelay(nextAttempts);

      await updateIntent(intent.userId, intentKey, {
        attempts: nextAttempts,
        lastError: errorMessage,
        nextRetryAtMs: Date.now() + nextRetryDelay,
      });

      console.warn('[OUTBOX] Intent replay failed, will retry:', intentKey, errorMessage);
      return false;
    }
  }, [scheduleInvalidation]);

  /**
   * Replay a single follow intent
   */
  const replayFollowIntent = useCallback(async (
    intent: UserFollowIntent,
    token: string
  ): Promise<boolean> => {
    const intentKey = getFollowIntentKeyForTarget(intent.targetUserId);
    const viewerId = intent.userId;

    try {
      const method = intent.desiredFollowing ? 'POST' : 'DELETE';
      const idempotencyKey = `${intent.desiredFollowing ? 'followUser' : 'unfollowUser'}:${intent.targetUserId}:${intent.id}`;

      const response = await fetch(`${API_BASE_URL}/users/${intent.targetUserId}/follow`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const is4xx = response.status >= 400 && response.status < 500;

        if (is4xx) {
          // Client error - remove from outbox (won't succeed on retry)
          console.warn('[OUTBOX] Removing failed follow intent (4xx):', intentKey, errorData.error || `HTTP ${response.status}`);
          await removeIntent(viewerId, intentKey);
          return false;
        }

        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Verify success
      if (!data.success) {
        throw new Error('Follow operation failed');
      }

      // Success - remove from outbox
      await removeIntent(viewerId, intentKey);

      // Reconcile counts: invalidate ACTIVE profile queries for target and viewer
      queryClient.invalidateQueries({
        predicate: (q) => {
          if (!Array.isArray(q.queryKey) || q.queryKey[0] !== 'profile') return false;
          const d = q.state.data as { id?: string } | undefined;
          return d?.id === intent.targetUserId || d?.id === viewerId;
        },
        refetchType: 'active',
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Server error or network error - update for retry
      const nextAttempts = intent.attempts + 1;
      const nextRetryDelay = calculateRetryDelay(nextAttempts);

      await updateIntent(viewerId, intentKey, {
        attempts: nextAttempts,
        lastError: errorMessage,
        nextRetryAtMs: Date.now() + nextRetryDelay,
      });

      console.warn('[OUTBOX] Follow intent replay failed, will retry:', intentKey, errorMessage);
      return false;
    }
  }, [queryClient]);

  /**
   * Replay a single reorder-move intent (with 409 retry)
   */
  const replayReorderMoveIntent = useCallback(async (
    intent: ListReorderMoveIntent,
    token: string
  ): Promise<boolean> => {
    const intentKey = getReorderIntentKeyForLookup(intent.watchlistId, intent.itemId);

    try {
      await apiFetch<{ success: boolean; new_revision?: number; new_rank?: string }>(
        `/watchlists/${intent.watchlistId}/reorder`,
        {
          method: 'PUT',
          token,
          headers: { 'Idempotency-Key': intent.idempotencyKey },
          body: {
            item_id: intent.itemId,
            insert_before_id: intent.insertBeforeId,
            snapshot_revision: intent.snapshotRevision,
          },
        }
      );

      // Success — remove from outbox
      await removeIntent(intent.userId, intentKey);

      // Invalidate the list query to pick up new revision/rank
      queryClient.invalidateQueries({ queryKey: ['lists', intent.watchlistId], exact: false });

      return true;
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const code = err instanceof ApiError ? err.code : undefined;

      // 409 Revision Conflict: refetch to get fresh revision, then retry once
      if (status === 409 && code === 'REVISION_CONFLICT') {
        try {
          // Fetch the current watchlist to get fresh revision
          const freshData = await apiFetch<{ watchlist: { revision?: number } }>(
            `/watchlists/${intent.watchlistId}?page=1&limit=1`,
            { token }
          );
          const freshRevision = freshData?.watchlist?.revision;

          if (typeof freshRevision === 'number') {
            // Persist fresh revision so future replays aren't guaranteed-stale
            await updateIntent(intent.userId, intentKey, {
              snapshotRevision: freshRevision,
              nextRetryAtMs: null, // Clear delay for immediate retry
            });

            // Retry with fresh revision
            await apiFetch<{ success: boolean; new_revision?: number }>(
              `/watchlists/${intent.watchlistId}/reorder`,
              {
                method: 'PUT',
                token,
                headers: { 'Idempotency-Key': intent.idempotencyKey },
                body: {
                  item_id: intent.itemId,
                  insert_before_id: intent.insertBeforeId,
                  snapshot_revision: freshRevision,
                },
              }
            );

            await removeIntent(intent.userId, intentKey);
            queryClient.invalidateQueries({ queryKey: ['lists', intent.watchlistId], exact: false });
            return true;
          }
        } catch (retryErr) {
          // Retry also failed — fall through to error handling
          const retryMsg = retryErr instanceof Error ? retryErr.message : 'Retry failed';
          console.warn('[OUTBOX] Reorder retry after 409 failed:', intentKey, retryMsg);
        }
      }

      // 4xx (non-409) — remove from outbox (won't succeed on retry)
      if (status >= 400 && status < 500 && status !== 409) {
        console.warn('[OUTBOX] Removing failed reorder intent (4xx):', intentKey);
        await removeIntent(intent.userId, intentKey);
        return false;
      }

      // Server/network error — update for retry with backoff
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const nextAttempts = intent.attempts + 1;
      const nextRetryDelay = calculateRetryDelay(nextAttempts);

      await updateIntent(intent.userId, intentKey, {
        attempts: nextAttempts,
        lastError: errorMessage,
        nextRetryAtMs: Date.now() + nextRetryDelay,
      });

      console.warn('[OUTBOX] Reorder intent replay failed, will retry:', intentKey, errorMessage);
      return false;
    }
  }, [queryClient]);

  /**
   * Replay all pending intents (list membership + follows + reorder moves)
   */
  const replayAll = useCallback(async () => {
    if (!user?.id || !session?.access_token) return;
    if (isReplayingRef.current) return;

    isReplayingRef.current = true;

    try {
      // Get all intent types
      const [listMembershipIntents, followIntents, reorderIntents] = await Promise.all([
        getListMembershipIntentsToReplay(user.id),
        getFollowIntentsToReplay(user.id),
        getReorderIntentsToReplay(user.id),
      ]);

      const totalIntents = listMembershipIntents.length + followIntents.length + reorderIntents.length;

      if (totalIntents === 0) {
        // No pending intents - stop interval
        if (replayIntervalRef.current) {
          clearInterval(replayIntervalRef.current);
          replayIntervalRef.current = null;
        }
        return;
      }

      // Replay list membership intents sequentially (deterministic order)
      for (const intent of listMembershipIntents) {
        await replayListMembershipIntent(intent, session.access_token);
      }

      // Replay follow intents sequentially
      for (const intent of followIntents) {
        await replayFollowIntent(intent, session.access_token);
      }

      // Replay reorder-move intents sequentially
      for (const intent of reorderIntents) {
        await replayReorderMoveIntent(intent, session.access_token);
      }

      // Start periodic replay if there are still pending intents
      const remainingCount = await getOutboxCount(user.id);
      if (remainingCount > 0 && !replayIntervalRef.current) {
        replayIntervalRef.current = setInterval(replayAll, REPLAY_INTERVAL_MS);
      } else if (remainingCount === 0 && replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
        replayIntervalRef.current = null;
      }
    } finally {
      isReplayingRef.current = false;
    }
  }, [user?.id, session?.access_token, replayListMembershipIntent, replayFollowIntent, replayReorderMoveIntent]);

  // Clear outbox on logout / user switch (prevents cross-user replay)
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;

    // Detect user change (logout or switch)
    if (prevUserId !== null && prevUserId !== currentUserId) {
      // Stop any running replay
      stopReplay();

      // Clear the previous user's outbox
      clearOutbox(prevUserId).catch((err) => {
        console.error('[OUTBOX] Failed to clear outbox on user change:', err);
      });
    }

    // Update ref for next comparison
    prevUserIdRef.current = currentUserId;
  }, [user?.id, stopReplay]);

  // Replay on mount (app boot)
  useEffect(() => {
    if (user?.id && session?.access_token) {
      // Delay slightly to let initial hydration complete
      const timeout = setTimeout(replayAll, 1000);
      return () => clearTimeout(timeout);
    }
  }, [user?.id, session?.access_token, replayAll]);

  // Replay on online
  useEffect(() => {
    const handleOnline = () => {
      replayAll();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [replayAll]);

  // Replay on focus
  useEffect(() => {
    const handleFocus = () => {
      replayAll();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [replayAll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
      }
      if (invalidationTimeoutRef.current) {
        clearTimeout(invalidationTimeoutRef.current);
      }
    };
  }, []);

  return { replayAll };
}
